import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeleteTenant, useSuspendTenant, useTenant, useUpdateTenant } from '../../api/hooks';
import { FormActions, FormRow, TagsInput } from '../../components/Form';
import { ConfirmDialog } from '../../components/Modal';
import { Panel } from '../../components/Panel';
import { StatusPill } from '../../components/StatusDot';
import { ErrorState, Loading, errorMessage } from '../../components/States';
import { useToast } from '../../components/Toast';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';
import { formatDateTime } from '../../lib/format';

export function SettingsPage() {
  const { tenantId, isMsp } = useTenantContext();
  const tenant = useTenant(tenantId);
  const update = useUpdateTenant(tenantId);
  const suspend = useSuspendTenant(tenantId);
  const remove = useDeleteTenant(tenantId);
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', contactName: '', contactEmail: '', timezone: '', notes: '', tags: [] as string[] });
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'suspend' | 'activate' | 'delete' | null>(null);

  useEffect(() => {
    if (tenant.data) {
      const t = tenant.data;
      setForm({ name: t.name, contactName: t.contactName ?? '', contactEmail: t.contactEmail ?? '', timezone: t.timezone ?? '', notes: t.notes ?? '', tags: t.tags });
    }
  }, [tenant.data]);

  if (tenant.isPending) return <Loading />;
  if (tenant.isError) return <ErrorState error={tenant.error} retry={() => tenant.refetch()} />;
  const t = tenant.data;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({ name: form.name.trim(), contactName: form.contactName.trim() || undefined, contactEmail: form.contactEmail.trim() || undefined, timezone: form.timezone.trim() || undefined, notes: form.notes.trim() || undefined, tags: form.tags });
      toast.push('Settings saved', 'success');
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const setStatus = async () => {
    try {
      if (confirm === 'suspend') await suspend.mutateAsync();
      else await update.mutateAsync({ status: 'active' });
      toast.push(confirm === 'suspend' ? 'Tenant suspended' : 'Tenant reactivated', 'success');
      setConfirm(null);
      if (confirm === 'suspend') navigate('/msp');
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    }
  };

  const deleteTenant = async () => {
    try {
      await remove.mutateAsync(t.slug);
      toast.push(`Tenant "${t.name}" deleted`, 'success');
      // Replace, so Back does not return to a tenant that no longer exists.
      navigate('/msp', { replace: true });
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    }
  };

  const field = (k: keyof typeof form) => ({ value: form[k] as string, disabled: !isMsp, onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value })) });

  return (
    <>
      <PageHeader title="General" subtitle={isMsp ? 'Tenant details are managed by the MSP.' : 'Tenant details are managed by your service provider. Contact them to make changes.'} actions={<StatusPill status={t.status} />} />
      <form onSubmit={submit}>
        <Panel title="Tenant">
          <div className="form">
            {error ? <div className="form-error">{error}</div> : null}
            <FormRow label="Name" required><input className="input" required minLength={2} maxLength={80} {...field('name')} /></FormRow>
            <FormRow label="Slug" hint="Immutable identifier used in URLs."><input className="input mono" disabled value={t.slug} /></FormRow>
            <FormRow label="Primary contact"><input className="input" {...field('contactName')} /></FormRow>
            <FormRow label="Contact email"><input className="input" type="email" {...field('contactEmail')} /></FormRow>
            <FormRow label="Time zone"><input className="input" {...field('timezone')} /></FormRow>
            <FormRow label="Tags"><TagsInput value={form.tags} disabled={!isMsp} onChange={(tags) => setForm((f) => ({ ...f, tags }))} /></FormRow>
            <FormRow label="Notes"><textarea className="textarea" {...field('notes')} /></FormRow>
            <FormRow label="Created"><div className="secondary" style={{ paddingTop: 7 }}>{formatDateTime(t.createdAt)} by {t.createdBy}</div></FormRow>
            {isMsp ? (
              <FormActions>
                {t.status === 'active' ? <button type="button" className="btn btn--danger" onClick={() => setConfirm('suspend')}>Suspend tenant</button> : <button type="button" className="btn" onClick={() => setConfirm('activate')}>Reactivate tenant</button>}
                <span className="grow" />
                <button type="submit" className="btn btn--primary" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save changes'}</button>
              </FormActions>
            ) : null}
          </div>
        </Panel>
      </form>
      {isMsp ? (
        <Panel title="Delete tenant">
          <div className="stack">
            <p className="secondary">
              Permanently removes the tenant, its users' accounts, its API key and all of its sites, sensors, cameras, alerts and history.
              {t.status === 'active' ? ' To keep the data, suspend the tenant instead.' : null}
            </p>
            <div>
              <button type="button" className="btn btn--danger" onClick={() => setConfirm('delete')}>Delete tenant</button>
            </div>
          </div>
        </Panel>
      ) : null}
      {confirm === 'delete' ? (
        <ConfirmDialog
          title="Delete tenant"
          message={<>Delete <strong>{t.name}</strong> permanently? Its users can no longer sign in, its API key is destroyed and all of its data is erased: sites, sensors, cameras, alerts, readings, licence plate reads and their pictures, and the event log. This cannot be undone.</>}
          confirmText={t.slug}
          confirmLabel="Delete permanently"
          danger
          busy={remove.isPending}
          onConfirm={deleteTenant}
          onClose={() => setConfirm(null)}
        />
      ) : confirm ? (
        <ConfirmDialog title={confirm === 'suspend' ? 'Suspend tenant' : 'Reactivate tenant'} message={confirm === 'suspend' ? <>Suspending <strong>{t.name}</strong> blocks all of its users from signing in and pauses polling. Data is kept.</> : <>Reactivate <strong>{t.name}</strong> so its users can sign in again.</>} confirmLabel={confirm === 'suspend' ? 'Suspend' : 'Reactivate'} danger={confirm === 'suspend'} busy={suspend.isPending || update.isPending} onConfirm={setStatus} onClose={() => setConfirm(null)} />
      ) : null}
    </>
  );
}
